import { AfterViewInit, Component, Injector, OnInit, ViewChild } from '@angular/core';
import { MortgageApplication } from '../../../models/mortgage-app.model';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import { NgForm } from '@angular/forms';
import * as _ from 'lodash';
import { catchError, map, Observable, of } from 'rxjs';
import { switchMap, tap } from "rxjs/operators";
import { GatherLeadSourceStep } from '../../../models/wizard/gather-lead-source-step.model';
import { Option } from '../../../models/wizard/option.model';
import { GatherLeadSourceStepConfig } from '../../../models/wizard/config/gather-lead-source-step-config.model';
import { BorrowerPortalAgentType } from '../../../models/borrower-portal-agent-type.model';
import { LeadSourceVisibility } from '../../../models/wizard/lead-source-visibility';
import { LeadSourceFields } from 'projects/borrower-app/src/app/models/wizard/lead-source-fields';
import { LeadSourceOptionLabels } from '../../../models/wizard/lead-source-option-labels';
import { ReferralSource } from '../../../models/referral-source.model';
import { LeadSourceValue } from 'projects/borrower-app/src/app/models/wizard/lead-source-value';
import { getOptionLabel } from 'projects/borrower-app/src/app/models/wizard/option-utils';

@Component({
    selector: 'gather-lead-source-step',
    templateUrl: 'gather-lead-source-step.component.html',
    standalone: false
})
export class GatherLeadSourceStepComponent
  extends WizardStepComponentBase<GatherLeadSourceStep>
  implements OnInit, AfterViewInit
{
  @ViewChild('leadSourceForm') leadSourceForm: NgForm | undefined;

  mortgage: MortgageApplication;

  leadSource: string = null;

  leadSourceType: string = null;

  leadSourceAgent: number = null;

  agentTypeId: number = null;

  protected referralAgents: AgentItem[] = null;
  protected referralAgentGroups: AgentGroup[] = [];

  protected agentTypes: BorrowerPortalAgentType[] = null;

  protected companies: string[] = [];

  protected referralSourceLabel = '';
  protected useManualAgentEntry = false;

  protected availableLeadSourceOptions: Option[] =
    GatherLeadSourceStepConfig.allLeadSourceOptions;

  protected readonly fields = LeadSourceFields;

  // The free-text referral source is joined to the lead-source type as
  // `${leadSourceType}-${leadSource}` before it is saved into Application.LeadSource, which is
  // NVARCHAR(100). The longest type label is 'Letter in the Mail' (18), so the worst case is
  // 18 + 1 + 50 = 69.
  protected readonly maxReferralSourceLength = 50;

  // Derived, never stored. It used to be a field updated only from the input's ngModelChange,
  // so every programmatic write to leadSource left it stale: switching lead-source type or
  // clearing the agent blanks the text without resetting the count (ENG-1852 case 1), and
  // returning to the step restores the text against a fresh 50 (ENG-1852 case 2).
  //
  // Floored at zero because stored values longer than the cap exist: the old keyboard-only
  // guard never enforced it on paste, drop or a programmatic write, and a stale count could
  // allow more typing after a restore. '-7 characters left' reads as a bug; the browser stops
  // them adding more, which is what the count is there to say.
  protected get noOfCharactersLeft(): number {
    return Math.max(0, this.maxReferralSourceLength - (this.leadSource?.length ?? 0));
  }

  protected valuesOfOptionsSelectedToBePresentedToUserAsChoices: string[] = [];

  constructor(injector: Injector) {
    super(injector);
    this.saveMortgageApplicationBeforeNextStep = true;
    this.mortgage = this.mortgageApplication;
    this.referralSourceLabel = this.translocoService.translate('wizard.leadSource.referralSource');
  }

  ngOnInit() {
    super.ngOnInit();
    this.populateAvailableOptions();
    this.refreshStoredOptionLabels();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.initializeLeadSource();
  }

  private isAgentLeadSourceType = () =>
    this.leadSourceType === 'Agents' || this.leadSourceType === 'Real Estate Agent';

  onLeadSourceChangedType = () => {
    this.leadSource = null;
    this.referralSourceLabel = this.getReferralSourceLabel();
    this.useManualAgentEntry = false;
    if (this.isAgentLeadSourceType()) {
      this.populateAgentTypeOptions().subscribe();
    } else {
      // A referral-link agent keeps the agent-type selector visible regardless of
      // lead-source type, so preserve the chosen agent type; only clear it when the
      // selector will actually hide (no referral-link agent).
      this.wizardFlowService.context.referralAgentId = null;
      if (!this.hasReferralLinkAgent()) {
        this.agentTypeId = null;
        this.wizardFlowService.context.agentTypeId = null;
      }
    }
    // The agent-list fetch must respect the admin's "Pick Agent Referral from a List"
    // toggle. Without this gate, the fetch fires even when the admin disabled the
    // dropdown — which 401s in register flow and triggers the AuthInterceptor's
    // global redirect-to-login.
    if (this.isAgentLeadSourceType() && this.step.pickAgentReferralFromAList) {
      this.startSpinner();
      const needToAddICannotFindMyAgent = !this.referralAgents;
      this.populateLeadSourceAgentOptions()
        .subscribe(() => {
          if (!this.useManualAgentEntry && needToAddICannotFindMyAgent) {
            this.referralAgents.unshift({
              id: -1,
              name: this.translocoService.translate('wizard.leadSource.cannotFindAgent'),
              company: '',
            });
            this.referralAgentGroups = this.buildReferralAgentGroups(this.referralAgents);
          }
        })
        .add(() => this.stopSpinner());
    } else {
      this.leadSourceAgent = null;
    }
  };

  onAgentChanged = () => {
    this.referralSourceLabel = this.getReferralSourceLabel();
    if (this.leadSourceAgent === -1) {
      this.leadSource = null;
    }
    if (this.hasSelectedListAgent()) {
      this.wizardFlowService.context.referralAgentId = this.leadSourceAgent;
    } else {
      this.wizardFlowService.context.referralAgentId = null;
      this.agentTypeId = null;
      this.wizardFlowService.context.agentTypeId = null;
    }
  };

  onAgentTypeChanged = () => {
    this.wizardFlowService.context.agentTypeId = this.agentTypeId || null;
  };

  // An actual agent is associated when the borrower picked a real agent from the list
  // (not the "I cannot find my agent" sentinel) or when a referral link pre-linked one.
  protected hasSelectedListAgent = (): boolean =>
    this.isAgentLeadSourceType() &&
    this.step.pickAgentReferralFromAList &&
    !this.useManualAgentEntry &&
    this.leadSourceAgent != null &&
    this.leadSourceAgent > 0;

  protected hasReferralLinkAgent = (): boolean => !!this.wizardFlowService.referralSource;

  protected shouldShowAgentTypeSelector = (): boolean =>
    !!this.agentTypes &&
    this.agentTypes.length > 0 &&
    (this.hasSelectedListAgent() || this.hasReferralLinkAgent());

  protected optionsSelectedToPresentToUserChanged = (
    option: Option,
    e: any
  ) => {
    if (e.target.checked) {
      if (
        !this.step.leadSourceOptions.find(
          (o) => o.name.toLowerCase() === option.name.toLowerCase()
        )
      ) {
        this.step.leadSourceOptions.push(option);
      }
    } else {
      const index = this.step.leadSourceOptions.findIndex(
        (o) => o.name.toLowerCase() === option.name.toLowerCase()
      );
      if (index >= 0) {
        this.step.leadSourceOptions.splice(index, 1);
      }
    }
  };

  /**
   * Brings this company's stored option labels up to date with the code catalogue, in both
   * languages - see LeadSourceOptionLabels for why a rename does not reach existing companies on
   * its own, and why only untouched labels are replaced.
   */
  private refreshStoredOptionLabels = () =>
    LeadSourceOptionLabels.refreshStoredLabels(
      this.step.leadSourceOptions,
      GatherLeadSourceStepConfig.allLeadSourceOptions
    );

  /** The company's own translation first, then the built-in one, then the English text. */
  protected leadSourceOptionLabel = (option: Option): string =>
    getOptionLabel(option, 'text', this.translocoService.getActiveLang());

  /**
   * Whether the free-text "who/what was it" box is shown. Unchanged for every pre-existing lead
   * source, including Personal Referral; the only new behaviour is that Returning Client asks
   * nothing.
   */
  protected shouldShowReferralTextInput = (): boolean =>
    LeadSourceVisibility.showsReferralTextInput(this.visibilityState());

  protected isAnswerRequired = (): boolean =>
    LeadSourceFields.isRequired(this.step.fieldConfig, LeadSourceFields.ANSWER);

  protected isAgentFromListRequired = (): boolean =>
    LeadSourceFields.isRequired(this.step.fieldConfig, LeadSourceFields.AGENT_FROM_LIST);

  /** Only the chosen lead source's setting applies; the box is hidden for every other one. */
  protected isReferralTextRequired = (): boolean =>
    LeadSourceFields.isFollowUpRequired(this.step.fieldConfig, this.leadSourceType);

  protected hasFollowUpSetting = (option: Option): boolean =>
    LeadSourceFields.hasFollowUpSetting(option._defaultsKey);

  private visibilityState = () => ({
    leadSourceType: this.leadSourceType,
    pickAgentReferralFromAList: this.step.pickAgentReferralFromAList,
    useManualAgentEntry: this.useManualAgentEntry,
    leadSourceAgent: this.leadSourceAgent,
  });

  private populateAvailableOptions = () => {
    this.availableLeadSourceOptions = GatherLeadSourceStepConfig.allLeadSourceOptions;
    this.valuesOfOptionsSelectedToBePresentedToUserAsChoices =
      this.step.leadSourceOptions.map((o) => o.name.toLowerCase());
  };

  private getReferralSourceLabel = (): string => {
    if (this.leadSourceType === 'Real Estate Agent') {
      return this.translocoService.translate('wizard.leadSource.whoIsCompanyAgent');
    } else if (this.leadSourceType === 'Social Media') {
      return this.translocoService.translate('wizard.leadSource.whichPlatform');
    } else if (this.leadSourceType === 'Internet') {
      return this.translocoService.translate('wizard.leadSource.whichWebsite');
    } else if (this.leadSourceType === 'Past Clients') {
      return this.translocoService.translate('wizard.leadSource.whoReferredYou');
    } else if (this.leadSourceType === 'Other') {
      return this.translocoService.translate('wizard.leadSource.pleaseSpecify');
    }
    return this.translocoService.translate('wizard.leadSource.referralSource');
  };

  onNextClicked() {
    this.wizardFlowService.context.leadSourceText = this.leadSource;
    this.wizardFlowService.context.leadSource = this.leadSource || this.leadSourceType;
    this.wizardFlowService.context.leadSourceType = this.leadSourceType;
    if (this.isAgentLeadSourceType()) {
      this.leadSource =
        this.leadSourceAgent == -1 || this.leadSourceAgent == null
          ? this.leadSource
          : this.leadSourceAgent?.toString();
      // Any agent answer says how they heard about us, even "I cannot find my agent" with the
      // optional name left blank. Only an unanswered agent list keeps the empty lead source it
      // has always sent.
      const skippedAgentList =
        this.step.pickAgentReferralFromAList && !this.useManualAgentEntry && this.leadSourceAgent == null;
      this.wizardFlowService.context.leadSource =
        this.leadSource || (skippedAgentList ? null : this.leadSourceType);
    }
    if (this.hasSelectedListAgent()) {
      this.wizardFlowService.context.referralAgentId = this.leadSourceAgent;
    }
    this.wizardFlowService.context.agentTypeId = this.shouldShowAgentTypeSelector()
      ? this.agentTypeId || null
      : null;
    const valid = this.validate();
    if (valid) {
      // The presence of applicationInfo is an indicator that we are in the 'apply' flow.
      if (this.applicationInfo) {
        if (
          (this.isAgentLeadSourceType() &&
            (this.leadSourceAgent == -1 || this.leadSourceAgent == null)) ||
          !this.isAgentLeadSourceType()
        ) {

          this.applicationInfo.leadSource = LeadSourceValue.compose(
            this.leadSourceType,
            this.leadSource
          );
        }

        this.startSpinner();
        this.mortgageApplicationService
          .saveLeadSource(
            this.mortgageApplication.applicationId,
            this.applicationInfo.leadSource
          )
          .pipe(switchMap(() => this.persistAgentExternalContactIfNeeded()))
          .subscribe(
            (result) => {
              this.stopSpinner();
              super.onNextClicked();
            },
            (error) => {
              this.stopSpinner();
            }
          );
      } else {
        this.saveMortgageApplicationBeforeNextStep = false;
        super.onNextClicked();
      }
    }
  }

  private initializeLeadSource = () => {
    if (this.applicationInfo) {
      const parsed = LeadSourceValue.parse(
        this.applicationInfo.leadSource,
        this.knownLeadSourceTypes()
      );
      if (parsed.leadSourceType) {
        this.leadSourceType = parsed.leadSourceType;
        this.leadSource = parsed.leadSource;
      }
    } else {
      this.leadSourceType = this.wizardFlowService.context.leadSourceType;
      // A chosen list agent travels as its id in leadSource. Anything else restores the typed text:
      // leadSource replaces an empty box with the type, which must not come back as an answer.
      const stored = this.wizardFlowService.context.leadSource;
      const storedAgentId = this.isAgentLeadSourceType() && !!stored && !isNaN(stored as any);
      this.leadSource = storedAgentId ? stored : this.wizardFlowService.context.leadSourceText;
    }

    if (!this.leadSource) {
      this.leadSource = null;
      this.leadSourceAgent = null;
    }

    this.useManualAgentEntry = false;

    this.agentTypeId = this.wizardFlowService.context.agentTypeId ?? null;

    if (this.isAgentLeadSourceType() || this.hasReferralLinkAgent()) {
      this.populateAgentTypeOptions().subscribe();
    }

    if (this.isAgentLeadSourceType() && this.step.pickAgentReferralFromAList) {
      this.startSpinner();
      this.populateLeadSourceAgentOptions()
        .subscribe(() => {
          if (this.useManualAgentEntry) {
            this.leadSourceAgent = null;
            return;
          }

          // No stored text is the manual-entry choice, not agent id Number(null) = 0. It was never a
          // pick from the list, though, so when the admin requires one the borrower makes it now.
          if (!this.leadSource) {
            this.leadSourceAgent = this.isAgentFromListRequired() ? null : -1;
          } else if (isNaN(this.leadSource as any)) {
            this.leadSourceAgent = -1;
          } else {
            this.leadSourceAgent = Number(this.leadSource);
          }
          // Insert an item in the first index
          this.referralAgents.unshift({
            id: -1,
            name: this.translocoService.translate('wizard.leadSource.cannotFindAgent'),
            company: '',
          });
          this.referralAgentGroups = this.buildReferralAgentGroups(this.referralAgents);
        })
        .add(() => this.stopSpinner());
    }
  };

  // Every type value a stored row can start with: today's catalogue, this company's (possibly older)
  // configured options, and retired values such as 'Agents' and 'Mailer' that old rows still carry.
  private knownLeadSourceTypes = (): string[] => [
    ...GatherLeadSourceStepConfig.allLeadSourceOptions.map((o) => o.value),
    ...(this.step.leadSourceOptions ?? []).map((o) => o.value),
    ...LeadSourceVisibility.AGENT_LEAD_SOURCES,
    ...LeadSourceVisibility.WITHOUT_SECONDARY_QUESTION,
  ].filter((value): value is string => typeof value === 'string');

  /**
   * The agent list has two sources, because this step runs on both sides of account creation.
   * With a loan, the authenticated endpoint resolves the company and loan officer from it. In the register
   * flow there is no loan yet - applicationId is 0 and there is no token - so the authenticated endpoint 401s
   * and the AuthInterceptor redirects the borrower to a login page. There the company comes from the register
   * link instead, and the list is public agents only (LLS-650).
   */
  private fetchReferralAgents = (): Observable<ReferralSource[]> => {
    if (this.mortgage?.applicationId) {
      return this.mortgageApplicationService.getReferralAgents(this.mortgage.applicationId);
    }

    const companyGuid = this.wizardFlowService.companyGuid;
    if (!companyGuid) {
      return of([]);
    }

    return this.mortgageApplicationService.getBorrowerPortalReferralAgents(companyGuid);
  };

  private populateLeadSourceAgentOptions = (): Observable<void> => {
    if (this.referralAgents) {
      return of(undefined);
    }

    return this.fetchReferralAgents()
      .pipe(
        tap((referralSources) => {
          if (!referralSources?.length) {
            this.referralAgents = null;
            this.referralAgentGroups = [];
            this.useManualAgentEntry = true;
            return;
          }

          const { companies, agents } = _.orderBy(
            referralSources,
            ['orgName', 'lasName'],
            ['asc', 'asc']
          ).reduce(
            (acc, agent) => {
              const company = agent.orgName;

              acc.companies.add(company);

              acc.agents.push({
                id: agent.agentId,
                name: `${agent.firstName} ${agent.lastName}`,
                company: company,
              });
              return acc;
            },
            {
              companies: new Set<string>(),
              agents: new Array<AgentItem>(),
            }
          );

          this.companies = Array.from(companies);
          this.referralAgents = agents;
          this.referralAgentGroups = this.buildReferralAgentGroups(agents);
        }),
        catchError(() => {
          this.referralAgents = null;
          this.referralAgentGroups = [];
          this.useManualAgentEntry = true;
          return of(undefined);
        }),
        map((_) => undefined)
      );
  };

  private populateAgentTypeOptions = (): Observable<void> => {
    if (this.agentTypes) {
      return of(undefined);
    }

    const companyGuid = this.wizardFlowService.companyGuid;
    if (!companyGuid) {
      return of(undefined);
    }

    return this.mortgageApplicationService
      .getBorrowerPortalAgentTypes(companyGuid)
      .pipe(
        tap((agentTypes) => {
          this.agentTypes = (agentTypes ?? []).filter((x) => x.showOnBorrowerPortal);
        }),
        catchError(() => {
          this.agentTypes = [];
          return of(undefined);
        }),
        map((_) => undefined)
      );
  };

  // In the 'apply' flow the application already exists, so account creation never runs and
  // cannot attach the agent/agent-type. Persist the external contact directly against the
  // existing loan whenever an agent (list-selected or referral-link) and agent type are chosen.
  private persistAgentExternalContactIfNeeded = (): Observable<void> => {
    const agentId = this.hasSelectedListAgent()
      ? this.leadSourceAgent
      : this.hasReferralLinkAgent()
        ? this.wizardFlowService.referralSource
        : null;
    const agentTypeId = this.wizardFlowService.context.agentTypeId;

    if (!agentId || agentId <= 0 || !agentTypeId || !this.mortgageApplication?.applicationId) {
      return of(undefined);
    }

    return this.mortgageApplicationService
      .upsertAgentExternalContact(this.mortgageApplication.applicationId, agentId, agentTypeId)
      .pipe(
        catchError(() => of(undefined)),
        map((_) => undefined)
      );
  };

  private buildReferralAgentGroups = (agents: AgentItem[]): AgentGroup[] => {
    const groupedAgents = new Map<string, AgentItem[]>();

    agents.forEach(agent => {
      const companyName = agent.company || 'Other';
      if (!groupedAgents.has(companyName)) {
        groupedAgents.set(companyName, []);
      }

      groupedAgents.get(companyName).push(agent);
    });

    return Array.from(groupedAgents.entries()).map(([label, items]) => ({
      label,
      items,
    }));
  };

  private validate = (): boolean => {
    if (this.leadSourceForm) {
      this.leadSourceForm.form.markAllAsTouched();
      if (!this.leadSourceForm.form.valid) {
        return false;
      }
    }
    return true;
  };
}

interface AgentItem {
  id: number;
  name: string;
  company: string;
}

interface AgentGroup {
  label: string;
  items: AgentItem[];
}

